import React, { forwardRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useDragControls } from 'framer-motion'
import { HiChevronLeft } from 'react-icons/hi2'
import { HiOutlineX } from 'react-icons/hi'

// Sits above the post composer modal (z-[10000]) and matches the desktop AI menu.
const SHEET_Z = 1000000000

/**
 * Native-feeling mobile bottom sheet: slides up from the bottom, grab handle,
 * drag-to-dismiss, backdrop, and a header with optional back button. The ref is
 * forwarded to the panel so an outside-click owner can treat the sheet as
 * "inside"; the sheet owns all dismissal (backdrop / drag / close button).
 */
const BottomSheet = forwardRef(({ isOpen, onClose, title, onBack, children }, ref) => {

    const dragControls = useDragControls()

    // Lock background scroll while open, restoring whatever was there before so
    // it composes with a parent modal that already locked.
    useEffect(() => {
        if (!isOpen) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = previous }
    }, [isOpen])

    const stopDrag = (e) => e.stopPropagation()

    return createPortal(
        <AnimatePresence>
            {isOpen && [
                <motion.div
                    key="ai-sheet-backdrop"
                    className="fixed inset-0 bg-black/60"
                    style={{ zIndex: SHEET_Z }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={onClose}
                />,
                <motion.div
                    key="ai-sheet-panel"
                    ref={ref}
                    className="fixed inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-2xl bg-secondary-500 shadow-2xl dark:bg-gray-800"
                    style={{ zIndex: SHEET_Z, paddingBottom: 'env(safe-area-inset-bottom)' }}
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                    drag="y"
                    dragControls={dragControls}
                    dragListener={false}
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={{ top: 0, bottom: 0.6 }}
                    onDragEnd={(e, info) => {
                        if (info.offset.y > 120 || info.velocity.y > 500) onClose()
                    }}
                >
                    {/* Grab handle + header are the drag-start zone. */}
                    <div onPointerDown={(e) => dragControls.start(e)} className="shrink-0 cursor-grab active:cursor-grabbing">
                        <div className="flex justify-center pb-1 pt-2.5">
                            <div className="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
                        </div>
                        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
                            {onBack ? (
                                <button
                                    type="button"
                                    onPointerDown={stopDrag}
                                    onClick={onBack}
                                    className="-ml-1 flex h-8 w-8 items-center justify-center rounded-full text-tertiary-700 active:bg-gray-100 dark:text-gray-200 dark:active:bg-gray-700"
                                >
                                    <HiChevronLeft className="text-xl" />
                                </button>
                            ) : (
                                <span className="h-8 w-8 shrink-0" />
                            )}
                            <span className="flex-1 truncate text-center text-[15px] font-semibold text-tertiary-800 dark:text-white">
                                {title}
                            </span>
                            <button
                                type="button"
                                onPointerDown={stopDrag}
                                onClick={onClose}
                                className="-mr-1 flex h-8 w-8 items-center justify-center rounded-full text-tertiary-700 active:bg-gray-100 dark:text-gray-200 dark:active:bg-gray-700"
                            >
                                <HiOutlineX className="text-lg" />
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto overscroll-contain">
                        {children}
                    </div>
                </motion.div>,
            ]}
        </AnimatePresence>,
        document.body
    )
})

export default BottomSheet
